// src/lib/shortClose.js
// #288, Session 74z3. The short-close mutation and the vocabulary behind it.
//
// Two pages call this. The buyer closes a line for receiving from the On order
// tab; AP closes it for billing from Invoicing. Both then refetch. That is the
// mutation/side-effect sequence duplicated across two callers, which is the
// case standing rule 14 says to extract — and it is not the case invoicing.js
// declined to extract, where one caller made extraction indirection rather than
// sharing.
//
// Three tables move together: the flag on the line, the audit row, and the
// request's status. So the write is a Postgres function called through rpc()
// under locked rule 7, never chained client-side .update() calls from here.

import { supabase } from './supabase';

// The two flags, spelled the way the database CHECK constraint spells them.
// Importing these rather than typing the strings is what stops a silent
// mismatch between a call site and the constraint.
export const RECEIPT = 'receipt';
export const INVOICE = 'invoice';

// Quick-pick reasons. These are the fast path and the free-text field is the
// record: picking a chip fills the field, so the two-tap mobile case works and
// anyone with more to say can still type it.
//
// They are NOT a constrained vocabulary in the database. receipt_close_reason
// is plain text on purpose — the reason has to survive a case nobody listed,
// and a CHECK constraint here would make the honest answer unsayable.
export const RECEIPT_CLOSE_REASONS = [
  'Vendor discontinued the item',
  'Vendor cancelled the balance',
  'Backorder abandoned',
  'Substituted on another order',
  'Damaged, not replacing',
];

export const INVOICE_CLOSE_REASONS = [
  'Vendor billed for the last time',
  'Balance credited, not billed',
  'Vendor wrote off the remainder',
  'Billed on another order',
];

export function closeReasonsFor(flag) {
  return flag === INVOICE ? INVOICE_CLOSE_REASONS : RECEIPT_CLOSE_REASONS;
}

// Wording differs by flag because the two flags are different acts by different
// people. The confirm button never reads "Confirm" — #288's whole shape is that
// receiving and billing are settled separately, and a shared verb would blur
// exactly the thing the split exists to keep apart.
export function closeCopyFor(flag) {
  if (flag === INVOICE) {
    return {
      title: 'No more billing expected',
      confirm: 'Close line for billing',
      reopen: 'Reopen for billing',
      prompt: 'Why is no further invoice coming?',
      closedLabel: 'Billing closed',
      settledVerb: 'closed this for billing',
      // AP's flag never blocks a later invoice, the same way the buyer's never
      // blocks a later receipt.
      note: 'This does not block anything. If the vendor bills again it can still be entered. It clears the outstanding amount, nothing more.',
    };
  }
  return {
    title: 'No more expected',
    confirm: 'Close line for receiving',
    reopen: 'Reopen for receiving',
    prompt: 'Why is the balance not coming?',
    closedLabel: 'Closed short',
    settledVerb: 'closed this for receiving',
    note: 'This does not block anything. If the balance turns up later it can still be received and still be invoiced. It clears the outstanding quantity, nothing more.',
  };
}

// BOTH of these mirror the database's settle rule rather than inventing their
// own, and that is the whole point of them.
//
// material_request_all_lines_settled() decides an order is finished when every
// line is settled for receiving AND for invoicing, where each half is "the flag
// is set OR the quantities say so". So the button must be offered on exactly
// the lines the derived half does NOT already settle. Offer it too narrowly and
// an order becomes permanently unclosable through the UI, which is #288's own
// defect reintroduced one layer up.

// Receiving: not settled while less arrived than was asked for. This
// deliberately includes a line with NO receipt at all — a vendor can cancel a
// line before shipping anything, and that is the same declaration.
export function canCloseForReceipt(it) {
  if (!it || it.receipt_closed) return false;
  const received = it.qty_received != null ? parseFloat(it.qty_received) : null;
  const requested = it.qty_requested != null ? parseFloat(it.qty_requested) : null;
  // The database's derived half requires qty_received IS NOT NULL, so a line
  // with either quantity missing can only ever be settled by the flag.
  if (received == null || requested == null) return true;
  return received < requested;
}

// Invoicing: not settled while something received is still unbilled — the ACM
// Panel case on plate 4, closed for receiving and still owing an invoice.
//
// The qty_received IS NULL case is why this is not simply "received > billed".
// A line that never arrived and was closed short by the buyer has a NULL
// qty_received, which fails the database's derived test outright, so ONLY the
// flag can settle it. An earlier cut of this returned false there — the button
// would have been hidden on precisely the line that needed it, and the order
// would have stayed open forever with nothing on screen explaining why.
export function canCloseForInvoice(it) {
  if (!it || it.invoice_closed) return false;
  const received = it.qty_received != null ? parseFloat(it.qty_received) : null;
  if (received == null) return true;
  const billed = (it.material_request_invoice_lines || [])
    .reduce((sum, l) => sum + (l.qty_invoiced != null ? parseFloat(l.qty_invoiced) : 0), 0);
  return received - billed > 0.0001;
}

export function isClosed(it, flag) {
  return flag === INVOICE ? !!it?.invoice_closed : !!it?.receipt_closed;
}

// The who/when/why behind a set flag, for the reversal panel. Returns null when
// the flag is not set — and also when it is set but the columns did not come
// back from the query, which is worth failing quietly rather than rendering
// "closed by undefined".
export function closeDetail(it, flag) {
  if (!isClosed(it, flag)) return null;
  const at = flag === INVOICE ? it.invoice_closed_at : it.receipt_closed_at;
  const reason = flag === INVOICE ? it.invoice_close_reason : it.receipt_close_reason;
  const by = flag === INVOICE ? it.invoice_closed_by_user : it.receipt_closed_by_user;
  return { at, reason, byName: by?.full_name || null };
}

// The mutation. Returns the function's jsonb result, which reports whether the
// request closed or reopened as a consequence — the caller uses that to decide
// what to say, not to decide what to write.
export async function setLineCloseFlag({ itemId, userId, flag, closed, reason }) {
  const { data, error } = await supabase.rpc('set_material_request_item_close_flag', {
    p_item_id: itemId,
    p_user_id: userId,
    p_flag: flag,
    p_closed: closed,
    p_reason: reason == null ? null : reason,
  });
  if (error) throw error;
  return data;
}
